import { Checkbox, Field } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ChangeEventWithData } from 'Components/Core/types';
import {
  IWithWorksheetInjectedProps,
  WorksheetGridFieldSection,
  WorksheetRecordGrid,
} from 'Components/Worksheets/Core';
import { prefixRecordFieldSetNames } from 'Components/Worksheets/Core/WorksheetRecordGrid/helpers';
import { createDryMassEntryOptionSelector } from 'Components/Worksheets/MoistureContentWorksheet';
import { getGridFieldSet as getMoistureContentGridFieldSet } from 'Components/Worksheets/MoistureContentWorksheet/BsEnIso/gridFieldSets';
import { actions } from 'Data/ReduxViewModels/WorksheetEditor';
import { valueOrDefault } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import { joinPaths } from 'Lib/Object/Path/shared';
import deepEquals from 'Lib/Object/deepEquals';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Trials can only be added/removed/excluded in groups of 2
 */
const trialGroupSize = 2;

let BsEnIsoPlasticLimitTrialSection: React.FC<IWithWorksheetInjectedProps> = ({
  namePrefix,
  createWorksheetSelector,
}) => {
  const dispatch = useDispatch();

  const dryMassEntryOption = useSelector(createDryMassEntryOptionSelector(createWorksheetSelector!));

  const preventTrialCreation = useSelector(
    pipe(
      createWorksheetSelector!('Trials'),
      valueOrDefault([] as Array<any>),
      (trials) => trials.filter((t) => !t.ExcludedTrial).length >= 2
    ),
    deepEquals
  );

  const trialRecordProps = useSelector(
    pipe(createWorksheetSelector!('Trials'), valueOrDefault([] as Array<any>), (trials) =>
      trials.map((t, i) => ({
        dim: t.ExcludedTrial,
        preventRemove: i < 2,
      }))
    ),
    deepEquals
  );

  const excludeTrials = (i: number, exclude: boolean) => {
    [...Array(trialGroupSize).keys()].forEach((k) =>
      dispatch(
        actions.setValue({
          name: joinPaths(namePrefix, 'Trials', `${i - k}`, 'ExcludedTrial'),
          value: exclude,
          sync: k === trialGroupSize - 1,
        })
      )
    );
  };

  const disableExcludeTrial = (i: number) => trialRecordProps.length - 1 !== i;

  return (
    <WorksheetGridFieldSection name="Trials">
      <WorksheetRecordGrid
        name={'Trials'}
        orientation={'horizontal'}
        recordProps={{
          width: 130,
          preventCreate: preventTrialCreation,
          preventRemove: (i) => trialRecordProps[i]?.preventRemove,
          dim: (i) => trialRecordProps[i]?.dim,
          removeConfirm: {
            confirm: true,
            confirmTitle: 'Trial Deletion',
            confirmText: 'Are you sure you want to delete this pair of trials?',
            confirmButtonText: 'Delete Trial',
          },
          recordGroupingSize: trialGroupSize,
        }}
        fieldSet={[
          ...prefixRecordFieldSetNames('MoistureDetermination', getMoistureContentGridFieldSet(dryMassEntryOption)),
          {
            name: 'ExcludedTrial',
            component: (i: number) =>
              i % trialGroupSize !== 0 ? (
                <Field
                  input={<Checkbox />}
                  onChange={(e: ChangeEventWithData) => excludeTrials(i, e?.data?.value)}
                  disabled={disableExcludeTrial(i)}
                />
              ) : (
                <></>
              ),
          },
        ]}
      />
    </WorksheetGridFieldSection>
  );
};

BsEnIsoPlasticLimitTrialSection = withInjectedProps(BsEnIsoPlasticLimitTrialSection);

export { BsEnIsoPlasticLimitTrialSection };
export default BsEnIsoPlasticLimitTrialSection;
